/** W82 Web-5h3b (HW12, D-666 Variante a): das Ziel einer Station öffnet über sich Platz für Figur und Blase, wie die Akten-Karte
 *  mit `--rede-platz` (D-420): ein Rand über dem Element, gemessen statt geschätzt. Was dabei die Lage wechselt (das Element und
 *  alles danach im Fluss), gleitet über die Lage nach (FLIP, `wechsel.sortieren` mit `mit`, Lage-und-Deckkraft-Regel, keine
 *  Höhe); reduziert und `?still` sofort. Jeder Wechsel startet an der Stelle, die man gerade sieht (Unterbrechen-Regel). */
import { DAUER, bewegungReduziert, kurve, still } from "./bewegung";
import type { SatzTeil } from "./blasenForm";
import type { Fassung } from "./einstellungen";

/** `oben`: Rand über dem Element; `unten`: Innenabstand unten in der Liste, aus der das Ziel als letztes gegangen ist (ein Rand
 *  fiele dort mit den Rändern der Karte zusammen und stünde unter ihr). */
export type Platz = {
  el: HTMLElement;
  seite: "oben" | "unten";
  hoehe: number;
  /** Rand oder Innenabstand vor dem Platz (Inline-Wert zum Zurückstellen, berechneter Wert als Grundlage) */
  vorher: string;
  grund: number;
  /** für die Übergabe, wenn das Element geht: gleicher Bezug, das nächste, die Liste, Abstand zum nächsten und eigene Höhe */
  schluessel: string | null;
  nach: Element | null;
  eltern: Element | null;
  abstand: number;
  eigen: number;
  /** was im Fluss vor dem Platz steht (seine Lage hängt nicht vom Platz ab) und seine Lage beim letzten Abgleich: geht das
   *  Element, zeigt der Anker, was im selben Einhängen darüber gewachsen ist */
  anker: HTMLElement | null;
  ankerY: number;
};

const laufend = new Set<Animation>();

/** Das Element, das im Fluss vor `el` steht: sein voriges Geschwister oder das eines Vorfahren bis `main`. */
function vorgaenger(el: Element): HTMLElement | null {
  for (let e: Element | null = el; e && e.tagName !== "MAIN" && e !== document.body; e = e.parentElement) {
    if (e.previousElementSibling instanceof HTMLElement) return e.previousElementSibling;
  }
  return null;
}

/** Was nach dem Element im Fluss steht: seine späteren Geschwister und die späteren Geschwister seiner Vorfahren bis `main`
 *  (wie `karteHalten.ts`). */
export function folgende(el: Element): HTMLElement[] {
  const aus: HTMLElement[] = [];
  for (let e: Element | null = el; e && e.tagName !== "MAIN" && e !== document.body; e = e.parentElement) {
    for (let s = e.nextElementSibling; s; s = s.nextElementSibling) if (s instanceof HTMLElement) aus.push(s);
  }
  return aus;
}

/** Lage im Dokument ohne Verschiebungen (`transform`, auch die eines laufenden Nachrückens): der Bezug des Mitgleitens. */
export function lageOhne(el: Element): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (let e = el instanceof HTMLElement ? el : null; e; e = e.offsetParent instanceof HTMLElement ? e.offsetParent : null) {
    x += e.offsetLeft;
    y += e.offsetTop;
  }
  return { x, y };
}

const eigenschaft = (seite: Platz["seite"]) => (seite === "oben" ? "margin-top" : "padding-bottom");

function setzen(p: Platz) {
  p.el.style.setProperty(eigenschaft(p.seite), `${p.grund + p.hoehe}px`);
}

function loesen(p: Platz) {
  if (p.vorher) p.el.style.setProperty(eigenschaft(p.seite), p.vorher);
  else p.el.style.removeProperty(eigenschaft(p.seite));
}

function anlegen(el: HTMLElement, seite: Platz["seite"], hoehe: number): Platz {
  const name = eigenschaft(seite);
  const st = getComputedStyle(el);
  const anker = vorgaenger(el);
  return {
    el,
    seite,
    hoehe,
    vorher: el.style.getPropertyValue(name),
    grund: parseFloat(seite === "oben" ? st.marginTop : st.paddingBottom) || 0,
    schluessel: el.getAttribute("data-geist-bezug"),
    nach: el.nextElementSibling,
    eltern: el.parentElement,
    abstand: 0,
    eigen: 0,
    anker,
    ankerY: anker ? lageOhne(anker).y : 0,
  };
}

/** Wie weit der Anker seit dem letzten Abgleich gerückt ist (ein Inhalt darüber wuchs); merkt die neue Lage. */
export function ankerVersatz(p: Platz): number {
  if (!p.anker?.isConnected) return 0;
  const y = lageOhne(p.anker).y;
  const um = y - p.ankerY;
  p.ankerY = y;
  return um;
}

/** Nachrücken: jedes Element gleitet von `dy` auf seine Lage, zur eigenen Lage addiert (composite add); ein Vorfahr in der
 *  Liste trägt seine Kinder schon, das Kind gleitet nur um den Rest. */
function gleiten(dys: Map<HTMLElement, number>, fassung: Fassung) {
  if (still() || bewegungReduziert()) return;
  for (const [el, dy] of dys) {
    let vorfahr = 0;
    for (let e = el.parentElement; e; e = e.parentElement) {
      const d = dys.get(e);
      if (d !== undefined) {
        vorfahr = d;
        break;
      }
    }
    const rest = dy - vorfahr;
    if (!el.isConnected || Math.abs(rest) < 0.5 || typeof el.animate !== "function") continue;
    const a = el.animate([{ transform: `translateY(${rest}px)` }, { transform: "translateY(0)" }], { duration: DAUER.wechsel.sortieren[fassung], easing: kurve("mit"), composite: "add" });
    laufend.add(a);
    const weg = () => laufend.delete(a);
    a.finished.then(weg, weg);
  }
}

/** Stellt den Platz um: `neu` öffnet Platz über `el` (null: kein Platz mehr). Gemessen wird die Lage, die man sieht (mit
 *  laufendem Nachrücken), dann beginnt das Nachrücken neu von dort. `bedarf` rechnet die Höhe aus den Lagen ohne Platz
 *  (Oberkante des Elements und sein Rand darüber), damit über ihm nichts liegt, was Figur oder Blase decken; `messen` liest
 *  die Lagen mit Platz, bevor das Nachrücken beginnt. */
export function platzStellen(
  alt: Platz | null,
  neu: { el: HTMLElement; bedarf: (oben: number, rand: number) => number } | null,
  fassung: Fassung,
  messen?: () => void,
): Platz | null {
  if (!alt && !neu) {
    messen?.();
    return null;
  }
  const menge = new Set<HTMLElement>();
  for (const p of [alt?.el, neu?.el]) if (p?.isConnected) [p, ...folgende(p)].forEach((e) => menge.add(e));
  for (const a of laufend) if (a.effect instanceof KeyframeEffect && a.effect.target instanceof HTMLElement && a.effect.target.isConnected) menge.add(a.effect.target);
  const vorher = new Map([...menge].map((e) => [e, e.getBoundingClientRect().top]));
  for (const a of laufend) a.cancel();
  laufend.clear();
  if (alt) loesen(alt);
  let platz: Platz | null = null;
  if (neu?.el.isConnected) {
    const p = anlegen(neu.el, "oben", 0);
    const oben = neu.el.getBoundingClientRect().top;
    p.hoehe = Math.max(0, neu.bedarf(oben, p.grund));
    setzen(p);
    // ein Rand, der mit dem des Vorfahren zusammenfällt, öffnet weniger als gesetzt: der Rest kommt dazu
    const fehlt = oben + p.hoehe - neu.el.getBoundingClientRect().top;
    if (fehlt > 0.5) {
      p.hoehe += fehlt;
      setzen(p);
    }
    const nachbar = p.nach instanceof HTMLElement ? p.nach : null;
    p.abstand = nachbar ? nachbar.getBoundingClientRect().top - neu.el.getBoundingClientRect().top : 0;
    p.eigen = neu.el.offsetHeight;
    platz = p;
  }
  const dys = new Map<HTMLElement, number>();
  for (const [e, top] of vorher) if (e.isConnected) dys.set(e, top - e.getBoundingClientRect().top);
  // Lagen ohne Nachrücken für den Aufrufer (Ziel der Figur, Bildlauf), bevor es beginnt
  messen?.();
  gleiten(dys, fassung);
  return platz;
}

/** Wächst die Blase über die Schätzung, wächst der Platz mit (gleitend wie beim Öffnen); ist sie niedriger (Akten-Form, Frage
 *  71 = A), schrumpft er ebenso, nie unter null. */
export function platzWachsen(p: Platz, um: number, fassung: Fassung) {
  if (!p.el.isConnected || Math.abs(um) < 0.5) return;
  const menge = [p.el, ...folgende(p.el)];
  const vorher = menge.map((e) => e.getBoundingClientRect().top);
  p.hoehe = Math.max(0, p.hoehe + um);
  setzen(p);
  gleiten(new Map(menge.map((e, i) => [e, (vorher[i] ?? 0) - e.getBoundingClientRect().top])), fassung);
}

/** Das Element des Platzes ist gegangen (abgehakte Frist, Karte nach dem Nachladen): der Platz bleibt, wo er war. Er geht an
 *  das Element mit demselben Bezug (Platzhalter der Karte), sonst an das nächste (es rückt an die Stelle), sonst unten in die
 *  Liste, aus der es ging; was nachrückt, gleitet um die gemessene Strecke. Ist auch die Liste fort, kein Platz mehr. */
export function platzUebergeben(p: Platz, fassung: Fassung): Platz | null {
  const gleich = p.schluessel ? document.querySelector<HTMLElement>(`[data-geist-bezug="${CSS.escape(p.schluessel)}"]`) : null;
  const nach = p.nach instanceof HTMLElement && p.nach.isConnected ? p.nach : null;
  const eltern = p.eltern instanceof HTMLElement && p.eltern.isConnected ? p.eltern : null;
  const el = gleich ?? nach ?? eltern;
  if (!el) return null;
  const seite = gleich || nach ? "oben" : "unten";
  const neu = { ...anlegen(el, seite, p.hoehe), abstand: p.abstand, eigen: p.eigen };
  setzen(neu);
  if (!gleich) {
    const dy = nach ? p.abstand : p.eigen;
    gleiten(new Map((nach ? [nach, ...folgende(nach)] : folgende(el)).map((e) => [e, dy])), fassung);
  }
  return neu;
}

/** Am Ende (Seite geht): Rand zurück, ohne Bewegung. */
export function platzLoesen(p: Platz | null) {
  if (p?.el.isConnected) loesen(p);
}

/** Schmalste Blase neben der Figur (320 px bei 200 % Schrift). */
const BLASE_MIN = 160;

/** W82 Web-5h-N1 (Frage 71 = A, D-677, Akten-Form): misst die Blase unterwegs mit Satz und Fuß („Danke“, Schritt, „Weiter“)
 *  unsichtbar in der Ebene, bevor der Platz aufgeht, in ihrer Breite, höchstens `raum` (die Blase steht links neben der Figur;
 *  bei 375 und 320 px wird sie dafür schmaler). Gleiche Klassen wie `Blase` und der Fuß in `GeistEbene`, damit Schrift, Innenabstand
 *  und Umbruch stimmen. */
export function blaseMessen(ebene: HTMLElement, teile: SatzTeil[], fuss: { schritt?: string; weiter: boolean }, raum: number): { breite: number; hoehe: number } {
  const halter = document.createElement("div");
  halter.className = "unterwegs-halter";
  halter.setAttribute("aria-hidden", "true");
  halter.style.visibility = "hidden";
  const b = document.createElement("div");
  b.className = "blase glas";
  b.dataset.form = "unterwegs";
  b.dataset.schwebend = "";
  const p = document.createElement("p");
  p.className = "blase-satz w-satz-unterwegs";
  for (const t of teile) {
    const teil = document.createElement(t.signal ? "em" : t.betont ? "b" : "span");
    if (t.signal) {
      teil.className = "signal";
      if (t.betont) teil.appendChild(document.createElement("b")).textContent = t.text;
      else teil.textContent = t.text;
    } else teil.textContent = t.text;
    p.appendChild(teil);
  }
  const unten = document.createElement("div");
  unten.className = "blase-fuss";
  const knopf = (text: string, art: string) => {
    const k = document.createElement("button");
    k.type = "button";
    k.className = "knopf";
    k.dataset.art = art;
    k.dataset.klein = "";
    k.textContent = text;
    return k;
  };
  if (fuss.schritt) {
    const s = document.createElement("span");
    s.className = "blase-schritt w-zustand";
    s.textContent = fuss.schritt;
    unten.appendChild(s);
  }
  const knoepfe = document.createElement("span");
  knoepfe.className = "blase-fuss-knoepfe";
  knoepfe.appendChild(knopf("Danke", "sekundaer"));
  if (fuss.weiter) knoepfe.appendChild(knopf("Weiter", "primaer"));
  unten.appendChild(knoepfe);
  b.append(p, unten);
  halter.appendChild(b);
  ebene.appendChild(halter);
  // nie schmaler als BLASE_MIN: lieber ragt sie ein Stück an die Figur, als dass jedes Wort eine Zeile bekommt
  const breite = Math.min(b.offsetWidth, Math.max(BLASE_MIN, Math.floor(raum)));
  b.style.width = `${breite}px`;
  const hoehe = b.offsetHeight;
  halter.remove();
  return { breite, hoehe };
}
